/**
 * CrashScreen — shown when the app fails to start or a fatal render error occurs.
 * Guarantees the user never sees a blank black/white screen.
 * This component uses ZERO external dependencies (no Tailwind runtime, no shadcn)
 * so it can render even if the CSS bundle is missing.
 */

import { getBootPhase, getBootError } from "@/lib/bootGuard";

declare const __BUILD_VERSION__: string;

interface CrashScreenProps {
  error?: Error | string | null;
  /** Extra context shown below the error */
  context?: string;
}

function getPlatformInfo(): string {
  try {
    const ua = navigator.userAgent || "unknown";
    const isCapacitor = /Capacitor/i.test(ua);
    return `Platform: ${isCapacitor ? "Native" : "Web"} | UA: ${ua.substring(0, 120)}`;
  } catch {
    return "Platform: unknown";
  }
}

function getBuildVersion(): string {
  try {
    return typeof __BUILD_VERSION__ !== "undefined"
      ? __BUILD_VERSION__
      : (window as any).__BUILD_VERSION__ || "unknown";
  } catch {
    return "unknown";
  }
}

export function CrashScreen({ error, context }: CrashScreenProps) {
  const errorMessage =
    error instanceof Error ? error.message : error || "Unknown error";
  const errorStack = error instanceof Error ? error.stack : null;
  const bootPhase = getBootPhase();
  const lastBootError = getBootError();
  const buildVersion = getBuildVersion();
  const platform = getPlatformInfo();
  const timestamp = new Date().toISOString();

  const debugInfo = [
    `Error: ${errorMessage}`,
    errorStack ? `Stack: ${errorStack.substring(0, 500)}` : null,
    `Boot Phase: ${bootPhase}`,
    lastBootError ? `Last Boot Error: ${lastBootError.substring(0, 300)}` : null,
    `Build: ${buildVersion}`,
    platform,
    `Time: ${timestamp}`,
    context ? `Context: ${context}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const handleReload = () => {
    try {
      window.location.reload();
    } catch {}
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(debugInfo);
      const btn = document.getElementById("tm-crash-copy-btn");
      if (btn) btn.textContent = "Copied!";
    } catch {
      // Fallback: select a textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = debugInfo;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        const btn = document.getElementById("tm-crash-copy-btn");
        if (btn) btn.textContent = "Copied!";
      } catch {}
    }
  };

  // Inline styles only — no Tailwind, no CSS file dependency
  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "#ffffff",
        color: "#1a1a1a",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            fontSize: 32,
          }}
        >
          ⚠️
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
          TransportMate failed to start
        </h1>
        <p style={{ fontSize: 14, color: "#666", margin: "0 0 16px" }}>
          The app encountered an error during startup. Please try reloading.
        </p>

        {/* Error details */}
        <div
          style={{
            background: "#f5f5f5",
            borderRadius: 8,
            padding: 12,
            textAlign: "left",
            fontSize: 11,
            lineHeight: 1.5,
            maxHeight: 200,
            overflow: "auto",
            wordBreak: "break-word",
            marginBottom: 16,
            fontFamily: "monospace",
          }}
        >
          <div><strong>Error:</strong> {errorMessage}</div>
          <div><strong>Boot Phase:</strong> {bootPhase}</div>
          <div><strong>Build:</strong> {buildVersion}</div>
          {lastBootError && (
            <div style={{ marginTop: 4 }}>
              <strong>Previous Error:</strong> {lastBootError.substring(0, 200)}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={handleReload}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
          <button
            id="tm-crash-copy-btn"
            onClick={handleCopy}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              background: "#e5e7eb",
              color: "#1a1a1a",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Copy Debug Info
          </button>
        </div>
      </div>
    </div>
  );
}
